import { ref } from 'vue'

interface SwipeOptions {
  isLoading: () => boolean
  onSwipeLeft: () => void
  onSwipeRight: () => void
  minDistance?: number
}

// `opts` is optional so the composable can be installed at the call site
// (App.vue binds touch handlers on the content wrapper) before swipe
// behaviour is wired. Without opts the handlers no-op — same observed
// behaviour as before this signature was introduced. See #111 for the
// follow-up wiring (day swipe).
export function useTouchHandling(opts?: SwipeOptions) {
  const touchStart = ref<number | null>(null)
  const isSwiping = ref(false)

  const handleTouchStart = (e: TouchEvent) => {
    if (!opts) return
    if (e.target instanceof HTMLElement) {
      if (e.target.closest('.v-tab') || e.target.closest('.v-btn')) return
    }
    if (!opts.isLoading()) {
      touchStart.value = e.touches[0].clientX
      isSwiping.value = true
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!opts) return
    if (!isSwiping.value || touchStart.value === null) return
    const dist = e.touches[0].clientX - touchStart.value
    if (Math.abs(dist) > 10 && !opts.isLoading()) {
      e.preventDefault()
    }
  }

  const handleTouchEnd = (e: TouchEvent) => {
    if (!opts) return
    if (!isSwiping.value || touchStart.value === null) return
    const touchEnd = e.changedTouches[0].clientX
    const min = opts.minDistance ?? 50
    if (!opts.isLoading()) {
      const dist = touchEnd - touchStart.value
      if (Math.abs(dist) > min) {
        dist > 0 ? opts.onSwipeRight() : opts.onSwipeLeft()
      }
    }
    isSwiping.value = false
    touchStart.value = null
  }

  return { handleTouchStart, handleTouchMove, handleTouchEnd }
}
